/**
 * Schémas Zod — Auth (login, signup, invite, 2FA).
 */

import { z } from "zod";

import { LIMITS, USER_ROLES } from "../constants.js";

import { EmailSchema, LocaleSchema, PhoneSchema } from "./common.js";

export const PasswordSchema = z
  .string()
  .min(LIMITS.PASSWORD_MIN, `Minimum ${LIMITS.PASSWORD_MIN} caractères`)
  .max(128)
  .regex(/[a-z]/, "Doit contenir au moins une minuscule")
  .regex(/[A-Z]/, "Doit contenir au moins une majuscule")
  .regex(/[0-9]/, "Doit contenir au moins un chiffre");

export const LoginInputSchema = z.object({
  email: EmailSchema,
  password: z.string().min(1, "Mot de passe requis"),
});
export type LoginInput = z.infer<typeof LoginInputSchema>;

export const SignupInputSchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
  full_name: z.string().min(2).max(120),
  tenant_slug: z.string().optional(), // pour s'enregistrer sur un tenant existant via invitation
  invitation_token: z.string().optional(),
});
export type SignupInput = z.infer<typeof SignupInputSchema>;

export const InviteUserInputSchema = z.object({
  email: EmailSchema,
  full_name: z.string().min(2).max(120),
  role: z.enum(USER_ROLES).default("broker"),
  locale: LocaleSchema,
  message: z.string().max(500).optional(),
});
export type InviteUserInput = z.infer<typeof InviteUserInputSchema>;

export const ResetPasswordRequestSchema = z.object({
  email: EmailSchema,
});

export const ResetPasswordConfirmSchema = z.object({
  token: z.string().min(20),
  new_password: PasswordSchema,
});

// 9.K.13 (D58.1-c refines techniques résiduels) — split base/refined pour
// parité 9.K.12 (P-2 ADR 0027 défense en profondeur). Pas de route API runtime
// `POST /v1/auth/change-password` actuellement (option b1 PO-Q-B 9.K.13 :
// pré-positionnement futur). Le wording du `.refine()` est en EN technique
// (P-2 ADR 0027 — codes machine en EN strict). Le wording user-facing
// bilingue sera porté par un code dédié `AUTH_PASSWORD_MUST_DIFFER` quand
// la route runtime sera implémentée (chunk dédié futur — hors 9.K.13).
export const ChangePasswordBaseSchema = z.object({
  current_password: z.string().min(1),
  new_password: PasswordSchema,
});

export const ChangePasswordSchema = ChangePasswordBaseSchema.refine(
  (d) => d.current_password !== d.new_password,
  {
    message: "new_password must differ from current_password",
    path: ["new_password"],
  },
);

// 2FA
export const Enable2FASchema = z.object({
  method: z.enum(["totp", "sms"]),
  phone: PhoneSchema.optional(),
});

export const Verify2FASchema = z.object({
  code: z.string().regex(/^\d{6}$/, "Code à 6 chiffres requis"),
});
